import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  form,
  FormField,
  maxLength,
  minLength,
  pattern,
  required,
} from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import {
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import type {
  CheckWorkspaceSlugApiResponse,
  CreateWorkspaceApiResponse,
  CreateWorkspaceRequest,
} from '@core/api/workspace/models';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_CHECK_DEBOUNCE_MS = 400;

type SlugAvailabilityStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

interface CreateWorkspaceFormModel {
  workspaceName: string;
  workspaceSlug: string;
}

function slugify(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

@Component({
  selector: 'app-create-workspace-page',
  imports: [FormField, SignalFormDirective, RouterLink, SignalTextField, SignalButton],
  templateUrl: './create-workspace-page.html',
  styleUrl: './create-workspace-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateWorkspacePage extends BaseComponent implements OnInit, OnDestroy {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);

  readonly createState = new State<CreateWorkspaceApiResponse>();
  readonly slugCheckState = new State<CheckWorkspaceSlugApiResponse>();
  readonly currentYear = new Date().getFullYear();
  readonly slugManuallyEdited = signal(false);
  readonly slugAvailabilityStatus = signal<SlugAvailabilityStatus>('idle');

  readonly workspaceModel = signal<CreateWorkspaceFormModel>({
    workspaceName: '',
    workspaceSlug: '',
  });

  readonly workspaceForm = form(this.workspaceModel, (schemaPath) => {
    required(schemaPath.workspaceName, { message: 'This field is required' });
    minLength(schemaPath.workspaceName, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.workspaceName, 100, { message: 'Maximum 100 characters allowed' });

    required(schemaPath.workspaceSlug, { message: 'This field is required' });
    minLength(schemaPath.workspaceSlug, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.workspaceSlug, 60, { message: 'Maximum 60 characters allowed' });
    pattern(schemaPath.workspaceSlug, SLUG_PATTERN, {
      message: 'Use lowercase letters, numbers, and hyphens only',
    });
  });

  readonly canSubmit = computed(() => {
    if (this.createState.loading()) {
      return false;
    }

    if (this.slugAvailabilityStatus() !== 'available') {
      return false;
    }

    const { workspaceName, workspaceSlug } = this.workspaceModel();
    return (
      workspaceName.trim().length >= 2 &&
      workspaceSlug.length >= 2 &&
      SLUG_PATTERN.test(workspaceSlug)
    );
  });

  private slugCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private slugCheckVersion = 0;

  ngOnInit(): void {
    if (!this.tokenStorage.isAuthenticated()) {
      this.router.navigate(['/signup']);
      return;
    }

    if (!this.tokenStorage.hasWorkspaceSession()) {
      this.router.navigate(['/create-company']);
    }
  }

  ngOnDestroy(): void {
    this.clearSlugCheckTimer();
  }

  onWorkspaceNameInput(value: string): void {
    if (!this.slugManuallyEdited()) {
      const slug = slugify(value);
      this.workspaceModel.update((model) => ({
        ...model,
        workspaceSlug: slug,
      }));
      this.scheduleSlugAvailabilityCheck(slug);
      return;
    }

    this.scheduleSlugAvailabilityCheck(this.workspaceModel().workspaceSlug);
  }

  onWorkspaceSlugInput(value: string): void {
    this.slugManuallyEdited.set(true);
    this.scheduleSlugAvailabilityCheck(value);
  }

  private scheduleSlugAvailabilityCheck(slug: string): void {
    this.clearSlugCheckTimer();

    const normalizedSlug = slug.trim();
    if (normalizedSlug.length < 2 || !SLUG_PATTERN.test(normalizedSlug)) {
      this.slugAvailabilityStatus.set('idle');
      return;
    }

    this.slugCheckTimer = setTimeout(() => {
      this.checkSlugAvailability(normalizedSlug);
    }, SLUG_CHECK_DEBOUNCE_MS);
  }

  private checkSlugAvailability(slug: string): void {
    const version = ++this.slugCheckVersion;
    this.slugAvailabilityStatus.set('checking');

    this.executeRequest({
      state: this.slugCheckState,
      request: this.userAPIService.checkWorkspaceSlugAvailability({ workspaceSlug: slug }),
      handleError: false,
      onSuccess: (response) => {
        if (version !== this.slugCheckVersion) {
          return;
        }

        const { isValid, available } = response.data;
        if (!isValid) {
          this.slugAvailabilityStatus.set('invalid');
        } else if (available) {
          this.slugAvailabilityStatus.set('available');
        } else {
          this.slugAvailabilityStatus.set('unavailable');
        }
      },
      onFailed: () => {
        if (version !== this.slugCheckVersion) {
          return;
        }

        this.slugAvailabilityStatus.set('idle');
      },
    });
  }

  private clearSlugCheckTimer(): void {
    if (this.slugCheckTimer) {
      clearTimeout(this.slugCheckTimer);
      this.slugCheckTimer = null;
    }
  }

  onSubmit(): void {
    if (!this.canSubmit() || !validateSignalForm(this.workspaceForm)) {
      return;
    }

    const { workspaceName, workspaceSlug } = this.workspaceModel();
    const payload: CreateWorkspaceRequest = {
      workspaceName,
      workspaceSlug,
    };

    this.executeRequest({
      state: this.createState,
      request: this.userAPIService.createWorkspace(payload),
      handleError: true,
      successMessage: 'Workspace created!',
      onSuccess: (response) => {
        this.tokenStorage.updateWorkspaceSession(response.data);
        this.router.navigate(['/dashboard']);
      },
    });
  }
}
